import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

// Mandatory, non-dismissible gate shown on login for members who signed up
// before the "self" sub_users mirror row existed (has_self_sub_user===false
// on the /auth/me payload — see server.public_user_full). Collects just the
// DOB; name/email/relationship are always derived server-side.
export default function SelfSubUserGate() {
    const { t } = useTranslation();
    const { user, refresh } = useAuth();
    const [dob, setDob] = useState(user?.profile?.date_of_birth || "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!dob) {
            setErr(t("signup.err_dob"));
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await api.post("/member/self-sub-user", { dob });
            await refresh();
        } catch (e) {
            setErr(formatApiError(e));
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6">
            <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-lg">
                <h2 className="text-lg font-semibold leading-none tracking-tight">
                    {t("self_sub_user_gate.title")}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                    {t("self_sub_user_gate.body")}
                </p>
                <form onSubmit={submit} className="mt-4 space-y-2">
                    <Label htmlFor="self-sub-user-dob">
                        {t("signup.field_dob")}
                    </Label>
                    <Input
                        id="self-sub-user-dob"
                        type="date"
                        data-testid="self-sub-user-dob"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        max={new Date().toISOString().split("T")[0]}
                        required
                        className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                    />
                    {err && <p className="text-sm text-red-600">{err}</p>}
                    <Button
                        type="submit"
                        disabled={busy}
                        className="w-full mt-2"
                        data-testid="self-sub-user-submit"
                    >
                        {busy ? t("signup.submit_busy") : t("self_sub_user_gate.submit")}
                    </Button>
                </form>
            </div>
        </div>
    );
}
